import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { formatDateTime, formatHms } from '../../modules/training/recordingDisplayUtils';

/**
 * Public (no-login) playback page for recordings marked Public.
 * Stream comes from /api/public/training/recordings/:id/stream
 */
export default function PublicTrainingRecordingPage() {
  const { id } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`/api/public/training/recordings/${id}`);
        if (!cancelled) setMeta(res.data?.data || null);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || 'Recording is not available');
          setMeta(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !meta) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
        <Alert severity="warning">{error || 'Recording not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        {meta.title?.name || 'Session recording'}
        {meta.edition_label ? ` · ${meta.edition_label}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'ui-monospace, monospace' }}>
        {meta.recording_label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recorded {formatDateTime(meta.recording_started_at)} → {formatDateTime(meta.recording_ended_at)} ·{' '}
        {formatHms(meta.duration_seconds)}
        {meta.available_until ? ` · Available until ${formatDateTime(meta.available_until)}` : ''}
      </Typography>
      <Box
        component="video"
        src={`/api/public/training/recordings/${id}/stream`}
        controls
        autoPlay
        sx={{ width: '100%', maxHeight: '80vh', bgcolor: '#000', borderRadius: 1 }}
      />
    </Box>
  );
}
